import React, { useCallback, useEffect, useRef } from "react";

import SmartImage from "../../../components/SmartImage";
import Skeleton from "../../../components/Skeleton";
import { formatPriceLabel } from "../../search/suppliersMappers";

const CylinderGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 2h6v3H9z" />
    <path d="M7.5 5h9a1.5 1.5 0 0 1 1.5 1.5V20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6.5A1.5 1.5 0 0 1 7.5 5Z" />
  </svg>
);

const RailCardSkeleton = () => (
  <div className="home-rail-card is-skeleton">
    <span className="home-rail-image">
      <Skeleton variant="block" />
    </span>

    <span className="home-rail-body">
      <span className="home-rail-name">
        <Skeleton variant="text" width="62%" height="14px" />
      </span>

      <span className="home-rail-divider" aria-hidden="true" />

      <span className="home-rail-meta">
        <Skeleton variant="text" width="88px" height="12px" />
        <Skeleton variant="text" width="62px" height="12px" />
      </span>
    </span>
  </div>
);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const CylinderRail = ({ cards, loading, activeVolume, onSelect }) => {
  const railRef = useRef(null);
  const cardRefs = useRef(new Map());

  const centreCard = useCallback((volume) => {
    const rail = railRef.current;
    const card = cardRefs.current.get(volume);

    if (!rail || !card) return;
    if (rail.scrollWidth <= rail.clientWidth) return;

    const railBox = rail.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();

    const delta =
      cardBox.left - railBox.left - (railBox.width - cardBox.width) / 2;

    if (Math.abs(delta) < 1) return;

    rail.scrollTo({
      left: rail.scrollLeft + delta,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, []);

  useEffect(() => {
    if (activeVolume === null || activeVolume === undefined) return;

    centreCard(activeVolume);
  }, [activeVolume, centreCard]);

  const select = useCallback(
    (card) => {
      centreCard(card.volume);
      onSelect(card);
    },
    [centreCard, onSelect]
  );

  if (loading) {
    return (
      <div className="home-rail" aria-hidden="true">
        {["a", "b", "c"].map((id) => (
          <RailCardSkeleton key={`rail-skeleton-${id}`} />
        ))}
      </div>
    );
  }

  if (!cards.length) return null;

  return (
    <div
      className="home-rail"
      ref={railRef}
      role="list"
      aria-label="Cylinder sizes nearby"
    >
      {cards.map((card) => {
        const active = activeVolume === card.volume;

        return (
          <button
            key={card.volume}
            type="button"
            role="listitem"
            ref={(node) => {
              if (node) cardRefs.current.set(card.volume, node);
              else cardRefs.current.delete(card.volume);
            }}
            className={`home-rail-card${active ? " is-active" : ""}`}
            aria-current={active || undefined}
            onClick={() => select(card)}
          >
            <span className="home-rail-image">
              {card.imageUrl ? (
                <SmartImage src={card.imageUrl} alt="" width="100%" height="100%" />
              ) : (
                <CylinderGlyph />
              )}
            </span>

            <span className="home-rail-body">
              <span className="home-rail-name">{card.volume}Kg Cylinder</span>

              <span className="home-rail-divider" aria-hidden="true" />

              <span className="home-rail-meta">
                <span className="home-rail-price">
                  {card.price !== null ? (
                    <>
                      from <b>{formatPriceLabel(card.price, card.currency)}</b>
                    </>
                  ) : (
                    "Price on request"
                  )}
                </span>

                <span className="home-rail-count">
                  {card.supplierCount}{" "}
                  {card.supplierCount === 1 ? "Supplier" : "Suppliers"}
                </span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default React.memo(CylinderRail);
